import { useContext, useEffect, useState } from 'react'
import { Warning, DeleteForever, Check, CancelPresentation } from '@mui/icons-material'
import { Backdrop, CircularProgress, CardContent, Card, CardActions, Checkbox, Autocomplete, Chip, FormControl, Grid, TextField, Typography, Stack, FormGroup, FormControlLabel, Dialog, DialogTitle, DialogActions, Button } from '@mui/material'
import { ROLES, getRoleById } from '../auth/role'
import RoleContext from '../auth/RoleContext'
import eventBus from './eventBus';
import UserRolesSelection from './UserRolesSelection'
import { getGroupMembership } from './data/membership'

export default function UserCard(props) {
  const roleDetails = useContext(RoleContext)
  const {
    // user,
    setDirty: propsSetDirty,
    manufacturerList,
    ownershipList,
    getOwnershipOptionLabel,
    groupConfig
  } = props

  const { roleGroupMappings, groupIdentities } = groupConfig

  // const setUser = props.onChange
  const [loading, setLoading] = useState(false)
  const [isDirty, _setDirty] = useState(false)
  const [groupsInitialized, setGroupsInitialized] = useState(null)

  const [user, setUser] = useState(props.user)
  const [ownInputValue, setOwnInputValue] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const confirmDelete = () => setConfirmOpen(true)

  const proceedDelete = async () => {
    setConfirmOpen(false)
    setDeleting(true)
    if (props.onDelete) {
      try {
        await props.onDelete()
      } catch (e) {
        // handle error if needed
      }
    }
    setDeleting(false)
  }

  const cancelDelete = () => setConfirmOpen(false)

  const validate = (u) => {
    const rid = u.role?.id ?? 0
    const errors = []

    if (rid === 0) {
      errors.push({prop: 'role', message: 'Role Selection is required'})
    } else if (rid === ROLES.BOTTLER.id && !(u.manufacturerUuids ?? []).length > 0) {
      errors.push({prop: 'manufacturerUuids', message: 'Facility selection(s) required'})
    } else if (rid === ROLES.BOTTLER_CORP.id && !(u.ownershipIds ?? []).length > 0) {
      errors.push({prop: 'ownershipId', message: 'Ownership selection(s) required'})
    }
    return errors
  }
  const handleSetUser = (u, localOnly = false) => {
    const errors = validate(u)
    const updatedUser = {...u, errors: errors}
    // const updatedUser = localOnly ? u : {...u, errors: errors, modified: true}
    setUser(updatedUser)

    const hasErrors = updatedUser.errors?.filter(e => e.prop !== 'save').length > 0    
    if (props.onChange !== undefined && !localOnly) {
      if (hasErrors) {
        props.onChange({...updatedUser, modified: false})
        setDirty(false)
        
      } else {
        props.onChange({...updatedUser, modified: true})
        setDirty(true)
        
      }
    }
  }

  const setDirty = (val) => {
    if (val === undefined) {
      _setDirty(true)
    } else {
      _setDirty(val)
    }
    // propsSetDirty(val)
  }

  const getApplicableGroups = (groupList, roleId) => {
    const roleMapping = roleGroupMappings.find(m => m.role === roleId)
    if (roleMapping) {
      let groupFilter = roleMapping.groups

      if (groupFilter) return groupList.filter((g) => groupFilter.includes(g.displayName))
    }
    return []
  }

  const getUserGroups = async (_user) => {
    const groupUser = _user ?? user
    const groups = await getGroupMembership({email: groupUser.email})
    const optionalGroups = getApplicableGroups(groups, groupUser.role?.id ?? 0)
    handleSetUser({ ...user, optionalGroups}, true)
  }

  useEffect(() => {
    const handleLoading = (loading) => {
      setLoading(loading)
    }

    if (user?.email && !groupsInitialized) {
      getUserGroups()
      setGroupsInitialized(true)
    }
    
    eventBus.on(`${user.email}-loading`, handleLoading)
    return () => {
      eventBus.removeListener(`${user.email}-loading`, handleLoading)
    }
  }, [user])

  useEffect(() => {
    if (props.user) {
      setUser(props.user)
    }
  }, [props.user])

  const handleRoleSelectChange = async (event) => {
    const rid = event.target.value
    const _role = getRoleById(rid)
    const _user = { ...user }
    _user.manufacturerUuids = []
    _user.ownershipIds = []
    
    if (_role) {
      let applicableGroups = getApplicableGroups(groupIdentities, rid)
      setGroupsInitialized(true)
      handleSetUser({ ..._user, role: { id: _role.id, name: _role.name }, optionalGroups: applicableGroups })
    } else {
      handleSetUser({ ..._user, role: { id: 0, name: '' }, optionalGroups: undefined })
    }
  }

  const isHigherLevelUser = (user) => {
    return user.role?.id > 0 && user.role?.id < roleDetails.role.id
  }

  const CustomField = (props) => {
    const { label, value, md = 12 } = props
    return <Grid item xs={12} md={md} style={{ overflow: 'hidden', alignItems: 'flex-start' }}>
      <Stack rowGap={0}>
        <Typography fontSize='0.75rem' color="textSecondary" variant="caption">{label}</Typography>
        <Typography fontSize='.9em' variant="body1" component="span">{value}</Typography>
      </Stack>
    </Grid>
  }

  const CardActionItem = (props) => {
    const styles = {
      container: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center', // if you want to fill rows left to right
        marginBottom: '0.2em',
      },
      label: {
        paddingRight: '0.5em',
      },
      action: {
        margin: 0,
        padding: 0,
      }
    }

    return <div style={styles.container} >
      <Typography style={styles.label} color="textSecondary" variant="caption">{props.label}</Typography>
      <div style={styles.action}>
        {props.children}
      </div>
    </div>
  }

  const GroupSelectionControls = () => {
    if (!groupIdentities) return
    const selections = getApplicableGroups(groupIdentities, user.role?.id ?? 0)

    return ( <> { selections.length > 0 &&
      <>
      <Typography variant="body1">Optional Groups</Typography>
      <FormGroup >
        {selections.map((group) => (
          <FormControlLabel
            key={`${user.koId}${group.id}`}
            sx={{ padding: '0', margin:'unset'}}
            control={
              <Checkbox
                size='small'
                checked={(user.optionalGroups ?? []).some(g => g.id === group.id)}
                onChange={(event, checked) => {
                  const userGroups = user.optionalGroups ?? []
                  if (checked) {
                    if (userGroups.findIndex(g => g.id === group.id ) === -1) {
                      handleSetUser({...user, optionalGroups: [...userGroups, group]})
                    }
                  } else {
                    handleSetUser({...user, optionalGroups: userGroups.filter((g) => g.id !== group.id)})
                  }
                }}
              />
            }
            label={group.displayName}
          />
        ))}
      </FormGroup>
      </>
      }
      </>
    )
  }

  return (
      <div key={user.email} sx={{}}>
        <Backdrop style={{ color: '#fff', zIndex: 2, position: 'absolute' }} open={loading || deleting}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Card style={{ marginBottom: '1em', padding: '5px 0', pointerEvents: deleting ? 'none' : 'auto' }}>
          <Grid container spacing={1} margin={'0 !important'} width={'100% !important'}>
            {props.editMode && <Grid item xs={1} sx={{
              borderRight: "solid 1px rgb(200 200 200)",
              display: 'flex',
              flexWrap: 'nowrap',
              flexDirection: 'row',
              alignContent: '',
              justifyContent: 'center',
              padding: '0 !important',
              alignItems: 'center',
            }}>
              <CardActions
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  
                }}
              ><Stack rowGap={2} alignItems='center'>
                {user.newUser && 
                <CardActionItem label="Notify">
                  <Checkbox
                    checked={user.notify}
                    style={{ margin: '0px', padding: '0px' }}
                    onChange={(e) => {
                      const _user = { ...user, notify: e.target.checked }
                      handleSetUser(_user)
                    }}
                  />
                </CardActionItem>
                }
                <CardActionItem label="Clear">
                  <CancelPresentation
                    color="primary"
                    style={{ cursor: 'pointer', fontSize: 26 }}
                    onClick={() => {
                      props.onClear()
                    }}
                  />
                </CardActionItem>
                {!user.newUser && roleDetails.isAdminUser() && 
                <CardActionItem label="Delete">
                  <DeleteForever
                    color="error"
                    style={{ cursor: 'pointer', fontSize: 26 }}
                    onClick={confirmDelete}
                  />
                </CardActionItem>
                }
                </Stack>
                {/* {user?.errors?.length > 0 &&
                  <CardActionItem>
                    <Warning
                      onClick={() => props.showErrors(user.errors?.map(e => e.message))}
                      color="error"
                      // style={{ marginLeft: "8px" }}
                    />
                  </CardActionItem>
                } */}
              </CardActions>
            </Grid>}
            <Grid item xs={props.editMode ? 11 : 12} sx={{padding: '0 !important'}}>
              <CardContent style={{ padding: '4px' }}>
                <Grid container spacing={0} margin={'0 !important'} width={'100% !important'}>
                  <Grid item xs={12} md={5.5} padding={'0 !important'}>
                    <Stack gap={1}>
                    <Grid container spacing={0}>
                      <CustomField md={6} label="Email" value={(user?.email || "").toLowerCase()} />
                      <CustomField md={2} label="KO ID" value={user?.koId} />
                      <CustomField md={4} label="Name" value={`${user?.firstName} ${user?.lastName}`} />
                    </Grid>
                        {user.status && user.status === 'success' &&
                          <Stack direction='row' alignItems='self-end' columnGap={1}>
                          <Check color='success' />
                          <Typography variant='body2' color='green'>Changes Saved!</Typography>
                          </Stack>
                        }
                        {user.errors && user.errors.length > 0 && <Stack direction='row' alignItems='self-end' columnGap={1}>
                          <Warning
                            onClick={() => props.showErrors(user.errors?.map(e => e.message))}
                            color="error"
                            // style={{ marginLeft: "8px" }}
                          />
                          <Stack rowGap={1}>
                            {user.errors.map(e => <Typography key={e.message.split(' ').join('')} variant='body2' color='error'>{e.message}</Typography>)}
                          </Stack>
                        </Stack>}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6.5}>
                    <Grid container columnGap={1}>
                      <Grid item xs={12} md={5.25}>
                        <Stack gap={.5}>
                        { !isHigherLevelUser(user) && <>
                          <UserRolesSelection label='Role: ' noSelectionLabel='Select A Role' onSelect={handleRoleSelectChange} value={`${(user?.role?.id !== undefined) ? user.role.id : 0}`} />
                          {/* <GroupSelectionControls /> */}
                          </>
                        }
                        {isHigherLevelUser(user) &&
                          <Typography>Role: {getRoleById(user?.role?.id)?.name ?? ''}</Typography>
                        }
                        </Stack>
                      </Grid>
                      {[ROLES.BOTTLER_CORP.id,ROLES.BOTTLER.id].includes(user?.role?.id) && 
                        <Grid item xs={12} md={6.5}>
                          {user?.role?.id === ROLES.BOTTLER_CORP.id && (ownershipList.length > 0) &&
                              <FormControl fullWidth size='small' >
                                <Autocomplete
                                  multiple
                                  id="ownership_autocomplete"
                                  size='small'
                                  options={ownershipList.filter(o => !user?.ownershipIds?.includes(o.value))}
                                  getOptionLabel={(option) => getOwnershipOptionLabel(option)}
                                  value={user?.ownershipIds}
                                  openOnFocus
                                  onChange={(_event, values) => {
                                    const _user = { ...user }
                                    const ownershipIds = values.map((v) => {
                                      if (typeof v === 'object' && v.value) {
                                        return v.value
                                      } else {
                                        return v
                                      }
                                    })
                                    handleSetUser({..._user, ownershipIds: ownershipIds })
                                    // handleFieldChange("ownershipIds", ownershipIds, _user, handleSetUser, setDirty)
                                  }}
                                  
                                  inputValue={ownInputValue}
                                  onInputChange={(e, v) => {
                                    setOwnInputValue(v)
                                  }}

                                  renderTags={(value, getTagProps) => {
                                    const _tags = value.map((option, index) => {
                                      const ownership = ownershipList.find((o) => o.value === option)
                                      return ownership ? (
                                        <Chip variant="filled" size='small' label={ownership.label} {...getTagProps({ index })} key={ownership.label}  sx={{ maxWidth: 'calc(100% - 50px) !important'}}/>
                                      ) : undefined
                                    })

                                    return _tags.filter(t => t)
                                  }
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      error={user?.role?.id === ROLES.BOTTLER_CORP.id && (!user?.ownershipIds || user?.ownershipIds.length === 0)}
                                      required={user?.role?.id === ROLES.BOTTLER_CORP.id}
                                      label="Associated Ownerships"
                                      InputLabelProps={{ shrink: user?.ownershipIds?.length > 0 || ownInputValue !== '' }}
                                    />
                                  )}
                                />
                              </FormControl>
                          }
                          {user?.role?.id === ROLES.BOTTLER.id && (manufacturerList.length > 0) &&
                            <FormControl fullWidth size='small' >
                              <Autocomplete
                                fullWidth
                                multiple
                                id="mfr_profile_autocomplete"
                                size='small'
                                options={manufacturerList.filter(m => !user?.manufacturerUuids?.includes(m.value))}
                                value={user?.manufacturerUuids}
                                openOnFocus
                                onChange={(_event, values) => {
                                  const _user = { ...user }
                                  const mfrUuids = values.map((v) => {
                                    if (typeof v === 'object' && v.value) {
                                      return v.value
                                    } else {
                                      return v
                                    }
                                  })
                                  handleSetUser({..._user, manufacturerUuids: mfrUuids})
                                }}
                                renderTags={(value, getTagProps) => {
                                  return value.map((option, index) => {
                                    const mfrUuid = manufacturerList.find((m) => m.value === option)
                                    return (
                                      <Chip variant="filled" size='small' label={mfrUuid.label} {...getTagProps({ index })} key={mfrUuid.label} sx={{ maxWidth: 'calc(100% - 50px) !important'}} />)
                                  }
                                  )
                                }
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    required={user?.role?.id === ROLES.BOTTLER.id}
                                    label="Associated Facilities"
                                    InputLabelProps={{ shrink: user?.manufacturerUuids?.length > 0 }}
                                    error={user?.role?.id === ROLES.BOTTLER.id && (!user?.manufacturerUuids || user?.manufacturerUuids.length === 0)}
                                  />
                                )}
                              />
                            </FormControl>
                          }

                        </Grid>
                      }
                      { !isHigherLevelUser(user) && <Grid item xs={12}>
                          <GroupSelectionControls />
                      </Grid>}
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Grid>
          </Grid>
        </Card>
        <Dialog open={confirmOpen} onClose={cancelDelete}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <Typography sx={{ px: 3, pb: 2 }}>Are you sure you want to delete this user?</Typography>
          <DialogActions>
            <Button onClick={cancelDelete}>Cancel</Button>
            <Button onClick={proceedDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    )
}